import { type ReactNode, useState } from 'react'

import { downloadRowsCsv } from '@/components/ui/table/exportRows'
import type { Filter } from '@/stores'
import { numberToCurrency as money } from '@/utils'
import { notCountedSentence } from '../AllAccountsCard/moneyText'
import { realizedListColumns } from '../columns'
import { type ListView, PortfolioTable } from '../PortfolioTable'
import { countOf, dateRangeText } from '../portfolioText'
import type { RealizedRow } from '../types'
import {
  accountLabelsOf,
  accountNamesOf,
  type DetailScope,
  notCountedWithin,
} from './detailScope'
import {
  realizedExport,
  realizedFilterSchema,
  realizedTotals,
  realizedView,
  type TermKey,
} from './realizedList'
import { TypeGroups } from './TypeGroups'

interface RealizedTabProps {
  scope: DetailScope
  slug: string
  renderStrip: (onExport: () => void) => ReactNode
}

const TERM_LABELS: Record<TermKey, string> = {
  all: 'All',
  short: 'Short term',
  long: 'Long term',
}

const sales = (count: number) => countOf(count, 'sale')

// The first and last sale date of the rows summed, so the label matches the sums.
const saleDateSpan = (rows: RealizedRow[]) => {
  const dates = rows
    .map((row) => row.liquidationDate)
    .filter((date): date is string => Boolean(date))
    .sort()
  return dates.length === 0
    ? null
    : { from: dates[0], to: dates[dates.length - 1] }
}

interface RealizedStripProps {
  rows: RealizedRow[]
  scope: DetailScope
}

const RealizedStrip = ({ rows, scope }: RealizedStripProps) => {
  const totals = realizedTotals(rows)
  const span = saleDateSpan(rows)
  const dates = span ? `Totals for ${dateRangeText(span)}` : 'Totals'
  const notCountedText = notCountedSentence(
    notCountedWithin(scope.notCounted, span, scope.baseQuery.accountIds),
  )
  const figures = [
    {
      label: 'Proceeds',
      value: totals.proceeds,
      detail: sales(totals.saleCount),
    },
    { label: 'Cost basis', value: totals.costBasis },
    {
      label: 'Short-term gain or loss',
      value: totals.shortTerm.amount,
      detail: sales(totals.shortTerm.count),
    },
    {
      label: 'Long-term gain or loss',
      value: totals.longTerm.amount,
      detail: sales(totals.longTerm.count),
    },
    { label: 'Net gain or loss', value: totals.net },
  ]
  return (
    <div className="flex flex-col gap-5">
      <p className="text-xs text-ink-subtle">
        {dates}, added up from the realized gain rows.
        {notCountedText && ` ${notCountedText}`}
      </p>
      <dl className="grid grid-cols-2 gap-6 md:grid-cols-5">
        {figures.map((figure) => (
          <div key={figure.label} className="min-w-0">
            <dt className="text-xs text-ink-subtle">{figure.label}</dt>
            <dd className="mt-0.5 tabular-nums">
              {figure.value == null ? (
                <span className="text-ink-subtle">Not printed</span>
              ) : (
                money(figure.value)
              )}
            </dd>
            {figure.detail && (
              <dd className="mt-0.5 text-xs text-ink-subtle">
                {figure.detail}
              </dd>
            )}
          </div>
        ))}
      </dl>
    </div>
  )
}

// Realized gains get the same treatment as Activity: a strip, groups, filters and a paged list.
export const RealizedTab = ({ scope, slug, renderStrip }: RealizedTabProps) => {
  const [term, setTerm] = useState<TermKey>('all')
  const accountNames = accountNamesOf(scope)
  const schema = realizedFilterSchema(accountNames)

  const view = (filters: Filter[], search: string): ListView<RealizedRow> => {
    const result = realizedView(scope.realized, schema, filters, search, term)
    return {
      rows: result.rows,
      key: JSON.stringify({ filters, search, term }),
      totals: <RealizedStrip rows={result.rows} scope={scope} />,
      groups: (
        <TypeGroups
          options={(['all', 'short', 'long'] as TermKey[]).map((key) => ({
            key,
            label: TERM_LABELS[key],
            count: result.termCounts[key],
          }))}
          active={term}
          onChange={setTerm}
        />
      ),
    }
  }

  return (
    <PortfolioTable
      contextId={`project-${slug}-portfolio-realized-list`}
      dialogId="portfolio-realized-list"
      schema={schema}
      getUniqueValues={(field) =>
        field === 'account' && accountNames
          ? Array.from(accountNames, ([value, label]) => ({ value, label }))
          : []
      }
      columns={realizedListColumns({ accountLabels: accountLabelsOf(scope) })}
      emptyMessage="No realized gains match these filters."
      view={view}
      renderStrip={(rows) =>
        renderStrip(() =>
          downloadRowsCsv(realizedExport(rows), `${slug}-realized-gains.csv`),
        )
      }
    />
  )
}
